import { FC, ChangeEvent } from "react"
import { RateFlowDetails } from "../../../../../../../../types"
import {
  CONFIG_ADMIN_INPUT_CLASS,
  ConfigAdminPanelStackedField,
  ConfigAdminToggleRow
} from "../../../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../../../config-admin-panel"

interface Props {
  values: RateFlowDetails
  setFormField: (name: string, value: any) => void
}

const RateflowGeneral: FC<Props> = ({ values, setFormField }) => {
  const updateSetting = (field: string, value: any) => {
    setFormField("settings", {
      ...values.settings,
      [field]: value
    })
  }

  return (
    <div className="space-y-6 pt-2">
      <ConfigAdminPanel>
        <ConfigAdminPanelStackedField
          label="Target Price"
          fieldKey="target_price"
          hint="Target price between 0-100 used for filtering rate results. Default is 100."
          htmlFor="rateflow-target-price"
        >
          <input
            id="rateflow-target-price"
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={Math.min(
              Math.max(values.settings?.target_price ?? 100, 0),
              100
            )}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const val = parseFloat(e.target.value)
              if (isNaN(val)) {
                updateSetting("target_price", 100)
              } else {
                updateSetting("target_price", Math.min(Math.max(val, 0), 100))
              }
            }}
            className={CONFIG_ADMIN_INPUT_CLASS}
          />
        </ConfigAdminPanelStackedField>

        <ConfigAdminPanelStackedField
          label="Cache TTL (seconds)"
          fieldKey="cache_ttl"
          hint="Duration for how long rate quote responses should be cached. Default is 3600 (1 hour)."
          htmlFor="rateflow-cache-ttl"
        >
          <input
            id="rateflow-cache-ttl"
            type="number"
            min="0"
            value={values.settings?.cache_ttl ?? 3600}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateSetting("cache_ttl", parseInt(e.target.value) || 3600)
            }
            className={CONFIG_ADMIN_INPUT_CLASS}
          />
        </ConfigAdminPanelStackedField>

        <ConfigAdminToggleRow
          label="Default Fees In"
          fieldKey="default_fees_in"
          description="Determines if fees should be included in the loan amount by default. Default is true."
          enabled={values.settings?.default_fees_in ?? true}
          onChange={(value: boolean) => updateSetting("default_fees_in", value)}
          last={false}
        />

        <ConfigAdminToggleRow
          label="Testing Mode"
          fieldKey="testing"
          description="Indicate if the environment is in testing mode. Default is false."
          enabled={values.settings?.testing ?? false}
          onChange={(value: boolean) => updateSetting("testing", value)}
          last={false}
        />

        <ConfigAdminPanelStackedField
          label="Prepaid Interest Days"
          fieldKey="prepaid_interest_days"
          hint="Number of prepaid interest days included in pricing calculations. Default is 5."
          htmlFor="rateflow-prepaid-days"
        >
          <input
            id="rateflow-prepaid-days"
            type="number"
            min="0"
            value={values.settings?.prepaid_interest_days ?? 5}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateSetting(
                "prepaid_interest_days",
                parseInt(e.target.value) || 5
              )
            }
            className={CONFIG_ADMIN_INPUT_CLASS}
          />
        </ConfigAdminPanelStackedField>

        <ConfigAdminPanelStackedField
          label="Lead Source"
          fieldKey="lead_source"
          hint="Integer value to identify the source of the lead for tracking and reporting"
          htmlFor="rateflow-lead-source"
          dividerBottom={false}
        >
          <input
            id="rateflow-lead-source"
            type="number"
            value={values.settings?.lead_source || ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateSetting("lead_source", parseInt(e.target.value) || null)
            }
            className={CONFIG_ADMIN_INPUT_CLASS}
          />
        </ConfigAdminPanelStackedField>
      </ConfigAdminPanel>
    </div>
  )
}

export default RateflowGeneral
